import { useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Link,
  Card,
  CardContent,
  Chip,
  Button,
  Divider
} from '@mui/material';
import { useAccessibility } from '../contexts/AccessibilityContext';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import SEO from '../components/SEO';

const ClothingGuide = () => {
  const { announce } = useAccessibility();

  // FAQ Structured Data for SEO
  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What colors should I wear to prevent bee and wasp stings?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Wear light-colored clothing such as white, tan, or gray. These colors are less likely to attract bees and wasps. Avoid bright colors like yellow, orange, and red, which can attract stinging insects.'
        }
      },
      {
        '@type': 'Question',
        name: 'What type of fabric is best for preventing insect stings?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Choose thick, smooth-finished fabrics like denim, canvas, or rip-stop nylon. These materials are more resistant to stings than thin or textured fabrics. Avoid wool, fur, and animal-origin materials that can attract insects.'
        }
      },
      {
        '@type': 'Question',
        name: 'Should I wear open-toed shoes when hiking?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No, you should wear closed-toe shoes that cover your entire foot and ankles. Tuck pants into socks or shoes to prevent insects from crawling inside. Avoid sandals or open shoes in grassy or flowering areas.'
        }
      },
      {
        '@type': 'Question',
        name: 'Do floral patterns attract bees and wasps?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, floral prints can mimic flowers and attract stinging insects. Choose solid-colored, light-colored clothing instead of floral patterns to reduce the risk of attracting bees and wasps.'
        }
      },
      {
        '@type': 'Question',
        name: 'Should I avoid wearing jewelry outdoors?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, shiny accessories can attract bees and wasps by mimicking the appearance of water or nectar. Avoid metallic jewelry, sequins, and reflective sunglasses when spending time outdoors.'
        }
      },
      {
        '@type': 'Question',
        name: 'Does perfume attract bees and wasps?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, scented products can attract stinging insects. Avoid using scented lotions, perfumes, or strongly fragranced hair products when going outdoors to reduce the risk of attracting bees and wasps.'
        }
      }
    ]
  };

  // HowTo Structured Data for prevention guide
  const howToStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Prevent Bee and Wasp Stings with Proper Clothing',
    description: 'A comprehensive guide to choosing the right clothing and accessories to prevent bee and wasp stings during outdoor activities.',
    step: [
      {
        '@type': 'HowToStep',
        name: 'Choose Light-Colored Clothing',
        text: 'Wear light-colored clothing such as white, tan, or gray. Avoid bright colors like yellow, orange, and red.',
        position: 1
      },
      {
        '@type': 'HowToStep',
        name: 'Select Thick, Smooth Fabrics',
        text: 'Choose thick materials like denim, canvas, or rip-stop nylon that are resistant to stings. Avoid wool and animal-origin materials.',
        position: 2
      },
      {
        '@type': 'HowToStep',
        name: 'Wear Protective Footwear',
        text: 'Wear closed-toe shoes that cover your entire foot and ankles. Tuck pants into socks to prevent insects from crawling inside.',
        position: 3
      },
      {
        '@type': 'HowToStep',
        name: 'Remove Shiny Accessories',
        text: 'Avoid metallic jewelry, sequins, and reflective sunglasses that can attract insects.',
        position: 4
      },
      {
        '@type': 'HowToStep',
        name: 'Secure Long Hair',
        text: 'Tie back long hair or wear a hat to prevent insects from getting trapped.',
        position: 5
      },
      {
        '@type': 'HowToStep',
        name: 'Avoid Floral Patterns',
        text: 'Choose solid-colored clothing instead of floral prints that can attract bees and wasps.',
        position: 6
      }
    ]
  };

  useEffect(() => {
    // Announce page load to screen readers
    announce("Clothing Guide page loaded", "polite");
  }, [announce]);

  return (
    <>
      <SEO
        title="Bee & Wasp Sting Prevention Clothing Guide"
        description="Expert guide on choosing the right clothing to prevent bee and wasp stings. Learn which colors, fabrics, and accessories to wear or avoid for safer outdoor adventures. Evidence-based recommendations from scientific sources."
        keywords="bee sting prevention clothing, wasp sting prevention, protective clothing colors, insect sting prevention, outdoor safety clothing, bee deterrent clothing, wasp deterrent clothing, hiking safety gear, camping protective clothing, beekeeping protective wear"
        type="article"
        structuredData={[faqStructuredData, howToStructuredData]}
      />
      <Box 
        sx={{ 
          py: { xs: 4, md: 6 }, 
          backgroundColor: 'background.default'
        }}
        role="main"
        aria-labelledby="clothing-guide-title"
      >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold', 
              color: 'primary.main',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
              mb: 2,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                width: '120px',
                height: '6px',
                backgroundColor: 'primary.main',
                bottom: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderRadius: '3px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }
            }}
            id="clothing-guide-title"
          >
            Prevent Stings
          </Typography>
          <Typography 
            variant="subtitle1" 
            color="text.secondary"
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.1rem' },
              mt: 3
            }}
          >
            The right clothing choices can significantly reduce your risk of bee and wasp stings.
            Follow these evidence-based recommendations to enjoy the outdoors safely.
          </Typography>
        </Box>

        {/* Main clothing recommendations */}
        <Grid container spacing={4}>
          {/* Light-Colored, Loose-Fitting Attire */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderTop: '4px solid',
                borderColor: 'primary.light'
              }}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    id="light-colors-heading"
                    sx={{ 
                      mb: 2,
                      color: 'primary.main',
                      fontWeight: 'medium'
                    }}
                  >
                    Light-Colored, Loose-Fitting Attire
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Recommended" 
                      color="success" 
                      size="small" 
                      icon={<CheckCircleOutlineIcon />} 
                      sx={{ mr: 1, mb: 1 }}
                    />
                  </Box>
                  
                  <Typography paragraph>
                    Wear light-colored clothing such as white, tan, or gray. These colors are less likely to attract bees and wasps. 
                    Loose-fitting clothing also prevents insects from getting trapped between fabric and skin.
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Colors to Avoid:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip 
                        label="Yellow" 
                        size="small" 
                        color="warning"
                        icon={<CloseIcon />} 
                      />
                      <Chip 
                        label="Orange" 
                        size="small" 
                        color="warning"
                        icon={<CloseIcon />} 
                      />
                      <Chip 
                        label="Red" 
                        size="small" 
                        color="warning"
                        icon={<CloseIcon />} 
                      />
                    </Box>
                  </Box>
                </Box>
                
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="caption" color="text.secondary">
                    Sources:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      startIcon={<LinkIcon />}
                      href="https://enviroliteracy.org/what-can-i-wear-to-protect-myself-from-wasps/?utm_source=chatgpt.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ fontSize: '0.75rem', textTransform: 'none', mr: 1 }}
                    >
                      Enviroliteracy.org
                    </Button>
                    <Button
                      startIcon={<LinkIcon />}
                      href="https://bee-health.extension.org/what-should-you-wear-to-protect-you-from-honey-bee-stings/?utm_source=chatgpt.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      Bee-health.extension.org
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Thick, Smooth-Finished Fabrics */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderTop: '4px solid',
                borderColor: 'secondary.main'
              }}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    id="fabrics-heading"
                    sx={{ 
                      mb: 2,
                      color: 'secondary.dark',
                      fontWeight: 'medium'
                    }}
                  >
                    Thick, Smooth-Finished Fabrics
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Recommended" 
                      color="success" 
                      size="small" 
                      icon={<CheckCircleOutlineIcon />} 
                      sx={{ mr: 1, mb: 1 }}
                    />
                  </Box>
                  
                  <Typography paragraph>
                    Choose thicker materials like denim, canvas, or rip-stop nylon. These fabrics are more resistant to stings than thin or textured fabrics.
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Materials to Avoid:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip 
                        label="Wool" 
                        size="small" 
                        color="warning"
                        icon={<CloseIcon />} 
                      />
                      <Chip 
                        label="Fur" 
                        size="small" 
                        color="warning"
                        icon={<CloseIcon />} 
                      />
                      <Chip 
                        label="Animal-origin materials" 
                        size="small" 
                        color="warning"
                        icon={<CloseIcon />} 
                      />
                    </Box>
                  </Box>
                </Box>
                
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="caption" color="text.secondary">
                    Sources:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      startIcon={<LinkIcon />}
                      href="https://enviroliteracy.org/what-can-i-wear-to-protect-myself-from-wasps/?utm_source=chatgpt.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ fontSize: '0.75rem', textTransform: 'none', mr: 1 }}
                    >
                      Enviroliteracy.org
                    </Button>
                    <Button
                      startIcon={<LinkIcon />}
                      href="https://beeculture.com/selecting-the-appropriate-protective-clothing/?utm_source=chatgpt.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      Beeculture.com
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Gloves and Footwear */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderTop: '4px solid',
                borderColor: 'info.main'
              }}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    id="footwear-heading"
                    sx={{ 
                      mb: 2,
                      color: 'info.dark',
                      fontWeight: 'medium'
                    }}
                  >
                    Gloves and Footwear
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Safety Essential" 
                      color="info" 
                      size="small" 
                      icon={<CheckCircleOutlineIcon />} 
                      sx={{ mr: 1, mb: 1 }}
                    />
                  </Box>
                  
                  <Typography paragraph>
                    Wear shoes that cover your entire foot and ankles. Tuck pants into socks or shoes to prevent insects from crawling inside. Avoid sandals or open shoes in grassy or flowering areas.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Closed-toe shoes</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Tuck pants into socks</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CloseIcon color="error" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Avoid sandals in flowering areas</Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="caption" color="text.secondary">
                    Sources:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      startIcon={<LinkIcon />}
                      href="https://beemanbuzz.com/a-beginners-guide-to-bee-and-wasp-protective-clothing/?utm_source=chatgpt.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      Beemanbuzz.com
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Avoid Shiny or Reflective Items */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderTop: '4px solid',
                borderColor: 'warning.light'
              }}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    id="shiny-items-heading"
                    sx={{ 
                      mb: 2,
                      color: 'warning.dark',
                      fontWeight: 'medium'
                    }}
                  >
                    Avoid Shiny or Reflective Items
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Important" 
                      color="warning" 
                      size="small" 
                      icon={<WarningIcon />} 
                      sx={{ mr: 1, mb: 1 }}
                    />
                  </Box>
                  
                  <Typography paragraph>
                    Shiny accessories can attract bees and wasps by mimicking the appearance of water or nectar. Avoid metallic jewelry, sequins, or reflective sunglasses.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CloseIcon color="error" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Metallic jewelry</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CloseIcon color="error" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Sequins and shiny fabrics</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CloseIcon color="error" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Highly reflective accessories</Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="caption" color="text.secondary">
                    Sources:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      startIcon={<LinkIcon />}
                      href="https://irescuebees.com/are-particular-colors-of-clothing-more-attractive.html?utm_source=chatgpt.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      iRescueBees.com
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Tie Back Long Hair */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderTop: '4px solid',
                borderColor: 'success.main'
              }}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    id="hair-heading"
                    sx={{ 
                      mb: 2,
                      color: 'success.dark',
                      fontWeight: 'medium'
                    }}
                  >
                    Tie Back Long Hair
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Easy Prevention" 
                      color="success" 
                      size="small" 
                      icon={<CheckCircleOutlineIcon />} 
                      sx={{ mr: 1, mb: 1 }}
                    />
                  </Box>
                  
                  <Typography paragraph>
                    Long or loose hair can trap insects, increasing the risk of stings. Tie back hair or wear a hat during outdoor activities.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Use hair ties or clips</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Wear hats for extra protection</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CloseIcon color="error" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Avoid loose, flowing hairstyles</Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="caption" color="text.secondary">
                    Sources:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      startIcon={<LinkIcon />}
                      href="https://www.ccohs.ca/oshanswers/biol_hazards/bees_wasps.html?utm_source=chatgpt.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      CCOHS.ca
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Avoid Floral Patterns */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderTop: '4px solid',
                borderColor: 'error.light'
              }}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    id="patterns-heading"
                    sx={{ 
                      mb: 2,
                      color: 'error.main',
                      fontWeight: 'medium'
                    }}
                  >
                    Avoid Floral Patterns
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Common Mistake" 
                      color="error" 
                      size="small" 
                      icon={<WarningIcon />} 
                      sx={{ mr: 1, mb: 1 }}
                    />
                  </Box>
                  
                  <Typography paragraph>
                    Floral prints can mimic flowers and attract stinging insects. Choose solid-colored clothing instead.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Solid, light-colored fabrics</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CloseIcon color="error" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Floral prints and patterns</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CloseIcon color="error" sx={{ mr: 1 }} fontSize="small" />
                      <Typography variant="body2">Flower-shaped accessories</Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="caption" color="text.secondary">
                    Sources:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      startIcon={<LinkIcon />}
                      href="https://enviroliteracy.org/what-is-the-one-color-to-wear-if-you-hate-wasps/?utm_source=chatgpt.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      Enviroliteracy.org
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Additional Tips Section */}
        <Box 
          sx={{ 
            mt: 6, 
            p: 4, 
            borderRadius: 2,
            backgroundColor: 'background.paper',
            boxShadow: 2
          }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold', 
              mb: 3,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                width: '60px',
                height: '4px',
                backgroundColor: 'primary.main',
                bottom: '-8px',
                left: 0
              }
            }}
            id="additional-tips-heading"
          >
            Additional Safety Tips
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', height: '100%' }}>
                <InfoIcon color="primary" sx={{ mt: 0.5, mr: 2, fontSize: '2rem' }} />
                <Box>
                  <Typography variant="h6" gutterBottom>Avoid Fragrances</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Do not use scented lotions, perfumes, or strongly fragranced hair products, as these may attract stinging insects.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', height: '100%' }}>
                <InfoIcon color="primary" sx={{ mt: 0.5, mr: 2, fontSize: '2rem' }} />
                <Box>
                  <Typography variant="h6" gutterBottom>Stay Calm</Typography>
                  <Typography variant="body2" color="text.secondary">
                    If a bee or wasp approaches, stay still or move slowly away. Swatting can provoke stings.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', height: '100%' }}>
                <InfoIcon color="primary" sx={{ mt: 0.5, mr: 2, fontSize: '2rem' }} />
                <Box>
                  <Typography variant="h6" gutterBottom>Inspect Clothing</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Shake out or inspect clothes, especially if left outside, to ensure insects aren't hiding inside.
                  </Typography>
                  <Link 
                    href="https://beemanbuzz.com/a-beginners-guide-to-bee-and-wasp-protective-clothing/?utm_source=chatgpt.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    variant="caption"
                    color="primary"
                    sx={{ display: 'inline-block', mt: 1 }}
                  >
                    Source: A Beginner's Guide to Bee and Wasp Protective Clothing
                  </Link>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default ClothingGuide; 